import styles from "./route-source-section.module.css";
import { useCallback, useState } from "react";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { ToolboxDeleteButton } from "../../toolbox-components/toolbox-component-delete-button";
import { GPXUploadButton } from "PosterEditor/common-components/gpx-upload-button";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
import { RouteSourceSectionModalStrava } from "./route-source-section-modals/route-source-section-modal-strava";
import { GPSUploadSection } from "../asset-import/gps/gps-upload-section";
import { useStravaActivities } from "strava-acquisition/hooks/use-strava-activities";
import { StravaConnectionBadge } from "./strava-connection-badge";
import { StravaActivitiesModal } from "PosterEditor/components/strava-activities-modal/strava-activities-modal";
import { useTouchMode } from "BEDROCK/mobile";

interface RouteSourceSectionProps {}

export const RouteSourceSection = (props: RouteSourceSectionProps) => {
  const { project, gpsSource } = usePosterProject();

  const getFirstUploadedFile = useCallback(() => {
    const first = gpsSource?.name;
    if (gpsSource) {
      return {
        name: first ?? "Unnamed route",
        numberOfPoints: gpsSource?.allCoordinates.length ?? 0,
      };
    }
  }, [gpsSource?.name]);

  const firstUploadedFile = getFirstUploadedFile();

  return (
    <div key={firstUploadedFile ? "uploaded" : "import"}>
      {firstUploadedFile ? (
        <UploadedFileList file={firstUploadedFile} />
      ) : (
        <CoordinateImport />
      )}
    </div>
  );
};

const CoordinateImport = () => {
  const { project, setCoordinates, gpsSource } = usePosterProject();
  const [GPXUploadFound, setGPXUploadFound] = useState(false);

  const sourceFileName = gpsSource?.name ?? " ";

  return (
    <div className={styles.container}>
      <GPSUploadSection
        onFoundGPX={(found) => {
          setGPXUploadFound(found);
        }}
      />
      <div className={styles.section}>
        {/* <img
          className={styles.gpxIcon}
          src="/pallete-components/GPX-ICON.svg"
          alt="GPX file icon"
        /> */}
        {/* <div className={styles.gpxExplainerTextContainer}>
          <p>
            GPX files contain coordinates of your activities, and can easily be
            found on <a href="https://strava.com">Strava.com</a>
          </p>
        </div>

        <a className={styles.gifContainer} href="https://strava.com">
          <img
            className={styles.importFromStravaGif}
            src="/hint-gifs/gpx-from-strava.gif"
            alt="GPX file from Strava"
          />
        </a> */}
        {/* <p>
          There are lots of ways to get your route into your project. Choose the
          option that best describes you:
        </p> */}
        <UploadOptionsList />
      </div>
    </div>
  );
};

export const FileUploadButton = ({
  handleFileUpload,
}: {
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  return (
    <div className={styles.uploadButtonWrapper}>
      <input
        id="file-upload"
        className={styles.uploadButton}
        type="file"
        accept=".gpx"
        onChange={handleFileUpload}
      />
      <label htmlFor="file-upload" className={styles.uploadButtonLabel}>
        SELECT A FILE
      </label>
    </div>
  );
};

interface UploadedFile {
  name: string;
  numberOfPoints: number;
}

interface UploadedFileListProps {
  file: UploadedFile;
}

const UploadedFileList = (props: UploadedFileListProps) => {
  const { deleteAllCoordinates } = usePosterProject();

  const numberOfPointsText =
    props.file.numberOfPoints === 1
      ? "1 point"
      : `${props.file.numberOfPoints} points`;

  const onClickDelete = () => {
    deleteAllCoordinates();
  };

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <div className={styles.uploadedFileContainer}>
          <div className={styles.uploadFileContainerTextContainer}>
            <span className={styles.uploadedFileTitle}>{props.file.name}</span>
            <span className={styles.uploadedFileSubtitle}>
              {numberOfPointsText}
            </span>
          </div>
          <ToolboxDeleteButton onclick={onClickDelete} />
        </div>
      </div>
    </div>
  );
};

interface UploadOptionsListProps {}

const UploadOptionsList = (props: UploadOptionsListProps) => {
  type ModalType = "gpx" | "strava" | "stravaActivities";
  const [modalType, setModalType] = useState<ModalType | undefined>();
  const { hasActivitiesData, clearActivitiesData, clearAllFilteredData } = useStravaActivities();
  const isTouchMode = useTouchMode();

  // Force re-render after disconnect by using a counter
  // This ensures hasActivitiesData() is re-evaluated after localStorage is cleared
  const [, forceUpdate] = useState(0);

  // Connected state is derived directly from localStorage via hasActivitiesData()
  // No need for separate hasDisconnected tracking since we clear the data on disconnect
  const isStravaConnected = hasActivitiesData();

  const handleDisconnect = () => {
    // Note: This is a UX-only disconnect that clears local data.
    // The Strava OAuth token is NOT revoked server-side. To properly revoke,
    // we would need to call POST https://www.strava.com/oauth/deauthorize
    // via an Edge Function (browser CORS prevents direct calls).
    // Since tokens expire after 6 hours (see PREVIEW_DATA_EXPIRY_MS), the
    // security window is limited. Users can also revoke access manually at
    // https://www.strava.com/settings/apps
    clearActivitiesData();
    clearAllFilteredData();
    // Force re-render to update isStravaConnected
    forceUpdate((n) => n + 1);
  };

  return (
    <div className={styles.uploadOptionsList}>
      {isStravaConnected ? (
        <>
          {/* Strava connected state wrapped in container */}
          <div className={styles.stravaConnectedContainer}>
            <StravaConnectionBadge
              onChooseActivity={() => {
                setModalType("stravaActivities");
              }}
              onDisconnect={handleDisconnect}
            />
          </div>
          {/* GPX upload option - hidden on mobile */}
          {!isTouchMode && (
            <UploadOptionSelectGPXFile
              onClick={() => {
                setModalType("gpx");
              }}
            />
          )}
        </>
      ) : (
        <>
          {/* Show Strava connect option when not connected */}
          <UploadOptionStrava
            onClick={() => {
              setModalType("strava");
            }}
          />
          {/* GPX upload option - hidden on mobile */}
          {!isTouchMode && (
            <UploadOptionSelectGPXFile
              onClick={() => {
                setModalType("gpx");
              }}
            />
          )}
        </>
      )}
      <RouteSourceSectionModalStrava
        isOpen={modalType === "strava"}
        onClose={() => setModalType(undefined)}
      />
      <StravaActivitiesModal
        isOpen={modalType === "stravaActivities"}
        onClose={() => setModalType(undefined)}
        onActivityApplied={() => {
          // Activity applied successfully - modal will close itself
        }}
      />
    </div>
  );
};

interface UploadOptionSelectGPXFileProps {
  onClick: () => void;
}
const UploadOptionSelectGPXFile = (props: UploadOptionSelectGPXFileProps) => {
  return (
    <GPXUploadButton>
      <UploadOption onClick={() => {}}>
        <div className={styles.uploadOptionContentDefault}>
          <UIIcon iconName="GPS" size={32} variant="REGULAR" />
          <div className={styles.uploadOptionContentText}>
            <h2>Upload a GPX file</h2>
            <p>You have a .GPX file on your computer you'd like to upload</p>
          </div>
        </div>
      </UploadOption>
    </GPXUploadButton>
  );
};

interface UploadOptionStravaProps {
  onClick: () => void;
}
const UploadOptionStrava = (props: UploadOptionStravaProps) => {
  return (
    <UploadOption onClick={props.onClick}>
      <div className={styles.uploadOptionContentDefault}>
        <UIIcon iconName="STRAVA" size={32} variant="REGULAR" />
        <div className={styles.uploadOptionContentText}>
          <h2>Import GPS from Strava</h2>
          <p>Connect to your Strava account to get a list of your activities</p>
        </div>
      </div>
    </UploadOption>
  );
};

interface UploadOptionAppleDeviceProps {
  onClick: () => void;
}
const UploadOptionAppleDevice = (props: UploadOptionAppleDeviceProps) => {
  return (
    <UploadOption onClick={props.onClick}>
      <div className={styles.uploadOptionContentDefault}>
        <UIIcon iconName="IPHONE-ACTIVITY" size={32} variant="REGULAR" />
        <div className={styles.uploadOptionContentText}>
          <h2>I tracked my run/hike/bike ride on my iPhone or Apple Watch</h2>
          <p>
            Download our helper app to turn your activity into a route and
            upload to your project
          </p>
        </div>
      </div>
    </UploadOption>
  );
};

interface UploadOptionFromPhotosProps {
  onClick: () => void;
}
const UploadOptionFromPhotos = (props: UploadOptionFromPhotosProps) => {
  return (
    <UploadOption onClick={props.onClick}>
      <div className={styles.uploadOptionContentDefault}>
        <UIIcon iconName="IPHONE-ROUTE" size={32} variant="REGULAR" />
        <div className={styles.uploadOptionContentText}>
          <h2>I'd like to make a route from photos on my iPhone</h2>
          <p>
            You went on a multi-day hike or road trip and took loads of photos
            on your iPhone. Download our app and we will help you make a route
            from the locations of your photos.
          </p>
        </div>
      </div>
    </UploadOption>
  );
};

interface UploadOptionProps {
  children: React.ReactNode;
  onClick: () => void;
}

const UploadOption = (props: UploadOptionProps) => {
  return (
    <button className={styles.uploadOption} onClick={props.onClick}>
      {props.children}
    </button>
  );
};
